import React, { FC } from 'react';
import { Feature, ShopWithAnnex } from '../../types';
import BookmarkButton from './BookmarkButton';
import FeatureTag from './FeatureTag';

export interface ShopCardProps {
  shop: any; // Shop / ShopWithAnnex
  onBookmark: (shopId: string) => void;
  onReserve: (shopId: string, shopName: string, preOpenDay: string) => void;
  isMapVersion: boolean;
}

const ShopCard: FC<ShopCardProps> = ({ shop, onBookmark, onReserve, isMapVersion = false }) => {
  const handleReserve = (e: React.MouseEvent) => {
    e.preventDefault();
    onReserve(shop.id, shop.name, shop.preOpenDay);
  };

  const cardRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (isMapVersion && cardRef.current) {
      const element = cardRef.current;
      element.style.setProperty('background-color', '#ffffff', 'important');
      element.style.setProperty('border-radius', '10px', 'important');
      element.style.setProperty('box-shadow', '0 5px 4px 0 rgba(0, 0, 0, 0.15)', 'important');
    }
  }, [isMapVersion]);

  return (
    <div ref={cardRef} className={`p-store-card ${isMapVersion ? 'p-store-card--map' : ''}`}>
      <button
        type="button"
        className="p-store-card__full-link"
        onClick={handleReserve}
        aria-label={`${shop.name}で予約する`}
      />

      {/* 店铺名称和收藏按钮 */}
      <div className="p-store-card__name p-store-card__name--mark">
        <div style={{ display: 'flex', alignItems: 'center', flex: '1 0 0', gap: '8px' }}>
          {shop.name && shop.name}
          {isMapVersion && shop.isAnnex !== undefined && shop.isAnnex === true && (
            <span className="c-genre-label">別館</span>
          )}
        </div>
        <BookmarkButton isBookmarked={shop.isBookmarked} onClick={() => onBookmark(shop.id)} />
      </div>

      <div className="p-store-card__body">
        {!isMapVersion && (
          <picture className="p-store-card__picture">
            <img loading="lazy" className="p-store-card__image" src={`${shop.image}`} alt={shop.name} />
          </picture>
        )}

        {(shop as ShopWithAnnex).isAnnex !== undefined ? (
          <div className="p-store-card__info">
            {shop.businessHours && shop.businessHours !== '' && (
              <p className="p-store-card__info-item" style={{ whiteSpace: 'pre-line' }}>
                <img src="/assets/img/icon-clock.svg" alt="営業時間" className="p-store-card__info-icon" />
                {shop.businessHours && shop.businessHours.replace(/\n+/g, '\n')}
              </p>
            )}
            {shop.phone && shop.phone !== '' && (
              <p className="p-store-card__info-item">
                <img src="/assets/img/icon-phone.svg" alt="電話番号" className="p-store-card__info-icon" />
                <a href={shop.phone ? `tel:${shop.phone.replace(/-/g, '')}` : ''} className="p-store-card__info-link">
                  {shop.phone}
                </a>
              </p>
            )}
            {shop.address && shop.address !== '' && (
              <p className="p-store-card__info-item">
                <img src="/assets/img/icon-map-black.svg" alt="住所" className="p-store-card__info-icon" />
                {shop.address}
              </p>
            )}
          </div>
        ) : (
          <div className="p-store-card__info">
            {shop.businessHours && shop.businessHours !== '' && (
              <p className="p-store-card__info-item" style={{ whiteSpace: 'pre-line' }}>
                <img src="/assets/img/icon-clock.svg" alt="営業時間" className="p-store-card__info-icon" />
                {shop.businessHours && shop.businessHours.replace(/\n+/g, '\n')}
              </p>
            )}
            {shop.phone && shop.phone !== '' && (
              <p className="p-store-card__info-item">
                <img src="/assets/img/icon-phone.svg" alt="電話番号" className="p-store-card__info-icon" />
                <a href={shop.phone ? `tel:${shop.phone.replace(/-/g, '')}` : ''} className="p-store-card__info-link">
                  {shop.phone}
                </a>
              </p>
            )}
            {shop.address && shop.address !== '' && (
              <p className="p-store-card__info-item">
                <img src="/assets/img/icon-map-black.svg" alt="住所" className="p-store-card__info-icon" />
                {shop.address}
              </p>
            )}
            {shop.annexOpenDate && shop.annexOpenDate !== '' && (
              <p className="p-store-card__info-item" style={{ whiteSpace: 'pre-line' }}>
                <img src="/assets/img/icon-clock.svg" alt="別館営業時間" className="p-store-card__info-icon" />
                {shop.annexOpenDate && `(別館)${shop.annexOpenDate.replace(/\n+/g, '\n')}`}
              </p>
            )}
            {shop.annexAddress && shop.annexAddress !== '' && (
              <p className="p-store-card__info-item">
                <img src="/assets/img/icon-map-black.svg" alt="別館住所" className="p-store-card__info-icon" />
                {shop.annexAddress && `(別館)${shop.annexAddress}`}
              </p>
            )}
          </div>
        )}
      </div>

      <div className={isMapVersion ? 'p-store-card__features--map' : 'p-store-card__features'}>
        {shop.features && shop.features.map((feature: Feature) => <FeatureTag key={feature.id} feature={feature} />)}
      </div>

      <button type="button" className="p-store-card__reservation" onClick={handleReserve}>
        この店舗で予約する
        <img src="/assets/img/icon-arrow-right-red.svg" alt="画像" className="p-icon p-icon--size14" />
      </button>
    </div>
  );
};

export default ShopCard;
